/**
 * Contas gerenciais e lançamentos (Task 16, brief 3.8).
 *
 * É o plano de contas GERENCIAL (não fiscal, não contábil) e os lançamentos que
 * compõem o resultado. Cada lançamento navega até o pedido/produto e à
 * conciliação que o originou (D-16.5).
 *
 * Gerencial, nunca fiscal: nada aqui emite nota nem apura imposto. O fiscal
 * oficial permanece no Bling/contábil.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca de sintaxe
 * (`createElement` → JSX) + ícone por bloco de seção via `SectionTitle`
 * (componente local, mesmo padrão de `ConciliacaoDetalhe.tsx`): esta tela não
 * tinha nenhum ícone antes (lacuna real). O nome acessível de cada bloco
 * continua sendo o texto do `h3` — o ícone é decorativo (`aria-hidden`).
 * Nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `KpiCard.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { PageHeader, StatTile } from "../ui/index.js";
import { Link } from "react-router-dom";
import { Landmark, Receipt, type LucideIcon } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { FinancialBoundary } from "../components/FinancialBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { SimulatorNote } from "./Conciliacao.js";
import { isFinancialSourceAwaitingContract } from "../financial/dataset.js";
import { MANAGERIAL_ACCOUNT_KIND_LABELS } from "../financial/managerial.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl } from "../format.js";
import type { FinancialDataset } from "../financial/dataset.js";
import type { FinancialViewProps } from "../financial/view-props.js";
import type { ManagerialAccount, ManagerialEntry } from "../api/types.js";

/** Título de seção com ícone decorativo (reforço visual, o `h3` segue o nome acessível do bloco). */
function SectionTitle({ Icon, children }: { Icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <h3 className="flex items-center gap-2 text-lg font-extrabold text-ink">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-brand-wash text-brand-text">
        <Icon className="h-4 w-4" aria-hidden focusable={false} />
      </span>
      {children}
    </h3>
  );
}

function AccountsTable({ rows, entries }: { rows: ManagerialAccount[]; entries: ManagerialEntry[] }): ReactElement {
  // Saldo por conta = soma SINALIZADA dos lançamentos daquela conta (a tela não
  // reinterpreta sinal; usa o que o servidor gravou pela convenção da regra).
  function balanceFor(account: ManagerialAccount): number {
    return entries.filter((entry) => entry.accountKey === account.key).reduce((sum, entry) => sum + entry.amountCents, 0);
  }
  return (
    <RecordTable<ManagerialAccount>
      bare
      caption="Plano de contas gerencial (não fiscal), com saldo do período"
      rows={rows}
      idOf={(row) => row.id}
      emptyMessage="Nenhuma conta gerencial cadastrada."
      columns={[
        { key: "name", label: "Conta", render: (row) => row.name },
        { key: "kind", label: "Tipo", render: (row) => <span data-account-kind={row.kind}>{MANAGERIAL_ACCOUNT_KIND_LABELS[row.kind]}</span> },
        {
          key: "balance",
          label: "Saldo",
          render: (row) => <span data-account-balance={balanceFor(row)}>{formatBrl(balanceFor(row))}</span>
        }
      ]}
    />
  );
}

function EntriesTable({ rows }: { rows: ManagerialEntry[] }): ReactElement {
  return (
    <RecordTable<ManagerialEntry>
      bare
      caption="Lançamentos gerenciais, com o pedido e a conciliação de origem"
      rows={rows}
      idOf={(row) => row.id}
      emptyMessage="Nenhum lançamento no período."
      columns={[
        { key: "account", label: "Conta", render: (row) => row.accountName },
        {
          key: "amount",
          label: "Valor",
          render: (row) => <span data-entry-amount={row.amountCents}>{formatBrl(row.amountCents)}</span>
        },
        {
          key: "order",
          label: "Pedido",
          render: (row) =>
            row.platformOrderId ? (
              <Link to={`/pedidos/${row.platformOrderId}`} className="font-semibold text-brand-text hover:underline">
                {row.orderExternalId ?? row.platformOrderId}
              </Link>
            ) : (
              "—"
            )
        },
        {
          key: "reconciliation",
          label: "Conciliação",
          render: (row) =>
            row.reconciliationId ? (
              <Link to={`/financeiro/conciliacao/${row.reconciliationId}`} className="font-semibold text-brand-text hover:underline">
                abrir
              </Link>
            ) : (
              "—"
            )
        },
        { key: "date", label: "Competência", render: (row) => row.entryDate }
      ]}
    />
  );
}

export function ContasGerenciaisView({ financial }: FinancialViewProps): ReactElement {
  if (isFinancialSourceAwaitingContract(financial)) {
    return (
      <div className="page page--contas-gerenciais flex flex-col gap-6" data-page="contas-gerenciais">
        <PageHeader title="Contas gerenciais" />
        <StatusPanel
          kind="awaiting_source"
          title={`Contas gerenciais ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota do financeiro não respondeu neste ambiente. Os lançamentos aparecem quando a rota for publicada."
        />
      </div>
    );
  }
  const { accounts, entries } = financial;
  return (
    <div className="page page--contas-gerenciais flex flex-col gap-6" data-page="contas-gerenciais">
      <PageHeader
        title="Contas gerenciais"
        description="Plano de contas de gestão (receita, taxa, comissão e custo) com os lançamentos do período."
      />
      <p className="sr-only" data-entry-count={entries.length}>
        {`${accounts.length} conta(s), ${entries.length} lançamento(s) no período.`}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatTile label="Contas" value={accounts.length} icon={Landmark} />
        <StatTile label="Lançamentos no período" value={entries.length} icon={Receipt} />
      </div>
      <SimulatorNote financial={financial} />
      <p className="page__note text-sm text-muted">
        Plano de contas GERENCIAL: a lente de gestão (receita/taxa/comissão/custo), não o livro fiscal. A emissão de
        nota e a apuração de impostos permanecem no Bling/contábil.
      </p>
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="contas">
        <SectionTitle Icon={Landmark}>Contas</SectionTitle>
        <AccountsTable rows={accounts} entries={entries} />
      </section>
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="lancamentos">
        <SectionTitle Icon={Receipt}>Lançamentos</SectionTitle>
        <EntriesTable rows={entries} />
      </section>
    </div>
  );
}

export function ContasGerenciais(): ReactElement {
  return <FinancialBoundary>{(financial: FinancialDataset) => <ContasGerenciaisView financial={financial} />}</FinancialBoundary>;
}
